import { Button, Divider, Tooltip, toast } from '@neo4j-ndl/react';
import { AURA_CONSOLE_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import {
  ACTION,
  type Instance,
  consoleApi,
  getApiError,
  isFetchBaseQueryError,
  useActiveProject,
  usePermissions,
} from '@nx/state';
import { SearchField } from '@nx/ui';
import { useState } from 'react';

import { useTrackUpxEvent } from '../../services/segment/analytics';
import { InstanceDatabasesList } from './instance-databases-table';

export const InstanceDatabasesSection = ({ instance }: { instance: Instance }) => {
  const trackEvent = useTrackUpxEvent();
  const activeProject = useActiveProject();
  const { isAllowed } = usePermissions();

  let enableCreateDatabase = false;
  let reasonCantCreateDatabase = '';

  if (!isAllowed(ACTION.CREATE, `namespaces/${activeProject.id}/aura-instances/${instance.id}/aura-databases`)) {
    reasonCantCreateDatabase = "You don't have permission to perform this action.";
  } else if (!instance.capabilities.multi_database.enabled) {
    reasonCantCreateDatabase = instance.capabilities.multi_database.message;
  } else if (instance.availableActions.add_database_to_instance === undefined) {
    reasonCantCreateDatabase = 'The ability to create a database in a multi-DB instance is not yet supported.';
  } else if (!instance.availableActions.add_database_to_instance.enabled) {
    reasonCantCreateDatabase = instance.availableActions.add_database_to_instance.message;
  } else {
    enableCreateDatabase = true;
  }

  const [filter, setFilter] = useState<string>('');

  const [addDatabase, addDatabaseRes] = consoleApi.useAddDatabaseMutation();

  const handleCreateDatabase = async () => {
    try {
      const createdDatabase = await addDatabase(instance.id).unwrap();

      trackEvent({
        event: AURA_CONSOLE_EVENTS.DATABASE_CREATE,
        properties: {
          instanceId: instance.id,
          auraDatabaseId: createdDatabase.id,
        },
        scope: APP_SCOPE.aura,
      });
    } catch (e) {
      if (isFetchBaseQueryError(e) && getApiError(e).reason === 'aura-instance-database-limit-exceeded') {
        toast.danger('You have reached the maximum number of databases for this instance.', { shouldAutoClose: true });
        return;
      }
      toast.danger('Failed to create database.', { shouldAutoClose: true });
    }
  };

  return (
    <>
      <div className="flex justify-between gap-2">
        <SearchField
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          htmlAttributes={{ 'aria-label': 'Search for databases' }}
        />
        <div className="flex shrink-0">
          <Tooltip type="simple" isDisabled={enableCreateDatabase}>
            <Tooltip.Trigger hasButtonWrapper>
              <Button
                onClick={() => {
                  void handleCreateDatabase();
                }}
                isDisabled={!enableCreateDatabase || addDatabaseRes.isLoading}
                isLoading={addDatabaseRes.isLoading}
              >
                Create database
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>{reasonCantCreateDatabase}</Tooltip.Content>
          </Tooltip>
        </div>
      </div>

      <Divider className="mt-4" />

      <InstanceDatabasesList instanceId={instance.id} filter={filter} />
    </>
  );
};
